/**
 * FaceSwapTab — Face Swap mode: dual preview + face presets + effect controls.
 * Composes from extracted hooks and subcomponents.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { ThreeColumn } from "../shell/Panel";
import { API_BASE } from "@/lib/api";
import { isLiteEdition } from "@/lib/config";
import { useFaceSwapLiveKit } from "@/hooks/useFaceSwapLiveKit";
import { useCameraDevices } from "@/hooks/useCameraDevices";
import { LeftFacePanel } from "../face/LeftFacePanel";
import { CenterPreview } from "../face/CenterPreview";
import { RightFacePanel } from "../face/RightFacePanel";
import { StudioFeatureModal } from "@/components/shared/StudioFeatureModal";
import { CameraPermissionGuard } from "@/components/shared/CameraPermissionGuard";
import { saveFaceProfile, getAllFaceProfiles } from "@/lib/faceVault";

export type SwapMode = "livekit" | "local";
export interface FaceProfile {
  profile_id: string;
  name: string;
  is_active: boolean;
  thumbnail_url?: string;
}

type EnhancerType = "none" | "gfpgan" | "codeformer";

interface FaceSwapTabProps {
  leftCollapsed: boolean;
  rightCollapsed: boolean;
  isMobile?: boolean;
  onSwappingChange: (v: boolean) => void;
  onProcessedStream: (s: MediaStream | null) => void;
  onActiveProfileChange: (name: string | null) => void;
  onFpsChange: (fps: number) => void;
  livekitSession?: import('@/hooks/useSessionDiscovery').LiveKitSession | null;
  onOpenProjector?: () => void;
  isProjectorOpen?: boolean;
}

export function FaceSwapTab({
  leftCollapsed, rightCollapsed, isMobile = false,
  onSwappingChange, onProcessedStream, onActiveProfileChange, onFpsChange, livekitSession,
  onOpenProjector, isProjectorOpen = false,
}: FaceSwapTabProps) {
  const videoRef = useRef<HTMLVideoElement>(null!);
  const outputRef = useRef<HTMLImageElement>(null!);

  const lk = useFaceSwapLiveKit();
  const { devices, selectedDeviceId, setSelectedDeviceId } = useCameraDevices();
  const [showCameraGuard, setShowCameraGuard] = useState(false);
  const [cameraErrorType, setCameraErrorType] = useState<"denied" | "not_found" | "unknown">("denied");
  const [isPiPActive, setIsPiPActive] = useState(false);
  const [swapMode, setSwapMode] = useState<SwapMode>("livekit");
  const [localSwapping, setLocalSwapping] = useState(false);

  const [hasStream, setHasStream] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState<string | null>(null);
  const [profiles, setProfiles] = useState<FaceProfile[]>([]);
  const [enhanceFace, setEnhanceFace] = useState(false);

  const [gfpganQuality, setGfpganQuality] = useState<"fast" | "high">("fast");
  const [enhancerType, setEnhancerType] = useState<EnhancerType>("none");
  const [codeformerFidelity, setCfFidelity] = useState(0.7);
  const [autoEnhancer, setAutoEnhancer] = useState<string>("none");
  const [eyedropperActive, setEyedropperActive] = useState(false);
  const [hardware, setHardware] = useState<any>(null);

  const [vcamActive, setVcamActive] = useState(false);
  const [vmicActive, setVmicActive] = useState(false);
  const [showStudioModal, setShowStudioModal] = useState(false);

  // Effective state based on swap mode
  const isLkActive = lk.status === "active" || lk.status === "waiting" || lk.status === "connecting";
  const effectiveSwapping = swapMode === "livekit" ? isLkActive : localSwapping;
  const effectiveFps = swapMode === "livekit" ? lk.fps : (localSwapping ? 30 : 0);
  const effectiveFrames = swapMode === "livekit" ? lk.frameCount : 0;

  // Sync to parent
  useEffect(() => { onSwappingChange(effectiveSwapping); }, [effectiveSwapping, onSwappingChange]);
  useEffect(() => { onFpsChange(effectiveFps); }, [effectiveFps, onFpsChange]);

  // Sync processed video stream to parent for Studio Projector
  useEffect(() => {
    onProcessedStream(lk.processedStream);
  }, [lk.processedStream, onProcessedStream]);

  // Restore cached profile on mount (checking IndexedDB face vault first, then localStorage)
  useEffect(() => {
    getAllFaceProfiles().then((stored) => {
      if (stored.length > 0) {
        const mapped: FaceProfile[] = stored.map((s) => ({
          profile_id: s.id,
          name: s.name,
          is_active: s.isActive,
          thumbnail_url: s.dataUrl,
        }));
        setProfiles((prev) => (prev.length === 0 ? mapped : prev));
        const active = mapped.find((m) => m.is_active);
        if (active) onActiveProfileChange(active.name);
      } else {
        const cached = localStorage.getItem("streampersona_active_profile");
        if (cached) {
          const p = JSON.parse(cached);
          setProfiles((prev) => (prev.length === 0 ? [p] : prev));
          onActiveProfileChange(p.name);
        }
      }
    }).catch(() => {});
  }, [onActiveProfileChange]);

  // Auto-connect LiveKit control plane immediately when session is discovered
  useEffect(() => {
    if (livekitSession?.roomName && livekitSession?.hostToken && livekitSession?.livekitUrl) {
      lk.connectRoom(livekitSession.roomName, livekitSession.hostToken, livekitSession.livekitUrl);
    }
  }, [livekitSession, lk]);

  // Sync profiles and dynamic hardware from LiveKit reactive status
  useEffect(() => {
    if (lk.engineStatus) {
      if (lk.engineStatus.profiles) {
        getAllFaceProfiles().then((stored) => {
          setProfiles((prev) => {
            return (lk.engineStatus?.profiles || []).map((remoteP: any, index: number) => {
              const localMatch = stored.find(s => s.id === remoteP.profile_id)
                || prev.find(p => p.profile_id === remoteP.profile_id)
                || stored[index]
                || prev[index];

              const displayName = (remoteP.name && remoteP.name !== "livekit_upload" && remoteP.name !== "uploaded")
                ? remoteP.name
                : (localMatch?.name || remoteP.name || `Face ${index + 1}`);

              return {
                profile_id: remoteP.profile_id,
                name: displayName,
                is_active: Boolean(remoteP.is_active),
                thumbnail_url: remoteP.thumbnail_url || (localMatch as any)?.dataUrl || (localMatch as any)?.thumbnail_url,
              };
            });
          });
        }).catch(() => {
          setProfiles(lk.engineStatus?.profiles || []);
        });

        const active = lk.engineStatus.profiles.find((p: FaceProfile) => p.is_active);
        if (active) onActiveProfileChange(active.name);
      }
      if (lk.engineStatus.hardware) {
        setHardware(lk.engineStatus.hardware);
      } else if (lk.engineStatus.gpu_name) {
        setHardware({
          gpu_name: lk.engineStatus.gpu_name,
          vram_gb: 32,
          is_cloud: true,
          platform: "Remote Cloud Node (RTX 5090 Blackwell)",
        });
      }
      if (lk.engineStatus.enhancer) {
        setAutoEnhancer(lk.engineStatus.enhancer);
        setEnhancerType(lk.engineStatus.enhancer as EnhancerType);
      }
      if (lk.engineStatus.enhance_face !== undefined) {
        setEnhanceFace(lk.engineStatus.enhance_face);
      }
      if (lk.engineStatus.gfpgan_quality) {
        setGfpganQuality(lk.engineStatus.gfpgan_quality as "fast" | "high");
      }
    }
  }, [lk.engineStatus, onActiveProfileChange]);

  // In Studio mode only: Poll local backend when disconnected
  useEffect(() => {
    if (isLiteEdition()) return;
    if (lk.status === "active") return;
    const poll = async () => {
      try {
        const r = await fetch(`${API_BASE()}/api/face/status`);
        if (r.ok) {
          const d = await r.json();
          const active = d.profiles?.find((p: FaceProfile) => p.is_active);
          setProfiles(d.profiles || []);
          onActiveProfileChange(active?.name || null);
        }
      } catch {}
    };
    poll();
    const id = setInterval(poll, 5000);
    return () => clearInterval(id);
  }, [lk.status, onActiveProfileChange]);

  // In Studio mode only: Fetch local system status
  useEffect(() => {
    if (isLiteEdition()) return;
    fetch(`${API_BASE()}/api/system/status`)
      .then(r => r.json())
      .then(data => {
        if (data.enhancer) {
          setAutoEnhancer(data.enhancer);
          setEnhancerType(data.enhancer);
        }
        if (data.enhance_face !== undefined) {
          setEnhanceFace(data.enhance_face);
        }
        if (data.gfpgan_quality) {
          setGfpganQuality(data.gfpgan_quality);
        }
        if (data.hardware) {
          setHardware(data.hardware);
        }
      })
      .catch(() => {});
  }, []);

  // Webcam
  const startWebcam = useCallback(async (overrideDeviceId?: string) => {
    try {
      const currentStream = videoRef.current?.srcObject as MediaStream;
      currentStream?.getTracks().forEach((t) => t.stop());

      const deviceIdToUse = overrideDeviceId ?? selectedDeviceId;
      const videoConstraint: MediaTrackConstraints = deviceIdToUse
        ? { deviceId: { exact: deviceIdToUse }, width: { ideal: 640 }, height: { ideal: 480 } }
        : { width: { ideal: 640 }, height: { ideal: 480 }, facingMode: "user" };

      const s = await navigator.mediaDevices.getUserMedia({
        video: videoConstraint,
        audio: false,
      });
      if (videoRef.current) {
        videoRef.current.srcObject = s;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play().then(() => setHasStream(true));
        };
      }
    } catch (e: any) {
      console.error("[FaceSwap] Camera error:", e);
      setCameraErrorType(e?.name === "NotFoundError" ? "not_found" : "denied");
      setShowCameraGuard(true);
    }
  }, [selectedDeviceId]);

  // Auto-start webcam preview on mount so camera is live immediately
  useEffect(() => {
    startWebcam();
  }, [startWebcam]);

  const handleSelectCamera = useCallback((id: string) => {
    setSelectedDeviceId(id);
    if (hasStream) {
      startWebcam(id);
    }
  }, [hasStream, setSelectedDeviceId, startWebcam]);

  const handleToggleVCam = useCallback(async () => {
    if (isLiteEdition()) {
      if (onOpenProjector) {
        onOpenProjector();
      }
      return;
    }
    const next = !vcamActive;
    setVcamActive(next);
    try {
      await fetch(`${API_BASE()}/api/virtualcam/${next ? "start" : "stop"}`, { method: "POST" });
    } catch (err) {
      console.error("[VirtualCam] Failed to toggle virtual camera:", err);
    }
  }, [vcamActive, onOpenProjector]);

  const handleTogglePiP = useCallback(async () => {
    const videoEl = lk.processedVideoRef.current || videoRef.current;
    if (!videoEl) return;
    try {
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
        setIsPiPActive(false);
      } else if (videoEl.requestPictureInPicture) {
        await videoEl.requestPictureInPicture();
        setIsPiPActive(true);
        videoEl.addEventListener("leavepictureinpicture", () => setIsPiPActive(false), { once: true });
      }
    } catch (e) {
      console.warn("[FaceSwap] PiP failed:", e);
    }
  }, [lk.processedVideoRef]);

  const handleToggleVMic = useCallback(async () => {
    if (isLiteEdition()) {
      setShowStudioModal(true);
      return;
    }
    const next = !vmicActive;
    setVmicActive(next);
    try {
      await fetch(`${API_BASE()}/api/virtualmic/${next ? "start" : "stop"}`, { method: "POST" });
    } catch (err) {
      console.error("[VirtualMic] Failed to toggle virtual mic:", err);
    }
  }, [vmicActive]);

  const stopWebcam = useCallback(() => {
    const s = videoRef.current?.srcObject as MediaStream;
    s?.getTracks().forEach(t => t.stop());
    if (videoRef.current) videoRef.current.srcObject = null;
    setHasStream(false);
    setLocalSwapping(false);
  }, []);

  // Upload face photo
  const handleUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 15 * 1024 * 1024) {
      setUploadMsg("✗ File size exceeds 15 MB limit");
      alert("File size exceeds 15 MB limit. Please choose a smaller file.");
      e.target.value = "";
      return;
    }
    
    // Reset input immediately so the same file can be re-selected
    e.target.value = "";
    setUploading(true); setUploadMsg(null);
    
    try {
      // Resize image on frontend to vastly speed up upload
      const bmp = await createImageBitmap(file);
      const maxSize = 1024;
      let { width, height } = bmp;
      if (width > maxSize || height > maxSize) {
        const scale = Math.min(maxSize / width, maxSize / height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }
      
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("No canvas context");
      ctx.drawImage(bmp, 0, 0, width, height);
      
      const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, "image/jpeg", 0.85));
      if (!blob) throw new Error("Compression failed");
      
      const cleanName = file.name.replace(/\.[^.]+$/, "");
      const saved = await saveFaceProfile(cleanName, blob);
      const arrBuffer = await blob.arrayBuffer();
      const uint8 = new Uint8Array(arrBuffer);
      const sentViaLiveKit = await lk.uploadReference(uint8, cleanName);
      if (sentViaLiveKit) {
        setUploadMsg(`✓ ${cleanName}`);
        // Optimistically activate profile so user can immediately click Start Swap
        const optimisticProfile: FaceProfile = {
          profile_id: saved.id,
          name: cleanName,
          is_active: true,
          thumbnail_url: saved.dataUrl,
        };
        try {
          localStorage.setItem("streampersona_active_profile", JSON.stringify(optimisticProfile));
        } catch {}
        setProfiles(prev => [optimisticProfile, ...prev.map(p => ({ ...p, is_active: false }))]);
        onActiveProfileChange(cleanName);
      } else if (!isLiteEdition()) {
        const form = new FormData();
        form.append("file", blob, file.name.replace(/\.[^.]+$/, ".jpg"));
        form.append("name", file.name.replace(/\.[^.]+$/, ""));
        
        const r = await fetch(`${API_BASE()}/api/face/prepare`, { method: "POST", body: form });
        const d = await r.json();
        setUploadMsg(r.ok ? `✓ ${d.name}` : `✗ ${d.detail}`);
        if (r.ok) {
          const sr = await fetch(`${API_BASE()}/api/face/status`);
          if (sr.ok) {
            const sd = await sr.json();
            setProfiles(sd.profiles || []);
            const active = sd.profiles?.find((p: FaceProfile) => p.is_active);
            onActiveProfileChange(active?.name || null);
          }
        }
      } else {
        throw new Error("Cloud GPU node is not connected yet. Please wait for LiveKit connection.");
      }
    } catch (err) { 
      setUploadMsg(`✗ ${err}`); 
    }
    setUploading(false);
  }, [lk, onActiveProfileChange]);

  const activeProfile = profiles.find(p => p.is_active);
  const canSwap = swapMode === "livekit"
    ? !!activeProfile
    : hasStream && !!activeProfile;

  const handleActivateProfile = useCallback(async (profileId: string) => {
    // 1. Try LiveKit DataChannel control message first (Zero-Inbound)
    const sentViaLK = await lk.sendControlMessage("activate_profile", { profile_id: profileId });
    if (!sentViaLK && !isLiteEdition()) {
      // 2. Fallback to HTTP REST (Local Studio mode)
      try {
        const r = await fetch(`${API_BASE()}/api/face/profiles/${profileId}/activate`, { method: "PUT" });
        if (r.ok) {
          const sr = await fetch(`${API_BASE()}/api/face/status`);
          if (sr.ok) {
            const sd = await sr.json();
            setProfiles(sd.profiles || []);
            const active = sd.profiles?.find((p: FaceProfile) => p.is_active);
            onActiveProfileChange(active?.name || null);
          }
        }
      } catch (e) {
        console.error("[FaceSwap] Failed to activate profile via HTTP:", e);
      }
    }
  }, [lk, onActiveProfileChange]);

  const handleStartSwap = useCallback(() => {
    if (swapMode === "livekit") {
      lk.startSwap(livekitSession?.roomName, livekitSession?.hostToken, livekitSession?.livekitUrl, selectedDeviceId);
    } else {
      setLocalSwapping(true);
    }
  }, [swapMode, lk, livekitSession, selectedDeviceId]);

  const handleStopSwap = useCallback(() => {
    if (swapMode === "livekit") lk.stopSwap();
    else setLocalSwapping(false);
  }, [swapMode, lk]);

  const updateToggle = useCallback(async (updates: Record<string, unknown>) => {
    // 1. Send via LiveKit DataChannel (Zero-Inbound)
    await lk.sendControlMessage("set_toggles", updates);
    // 2. Also send via REST (Local Studio mode)
    try {
      await fetch(`${API_BASE()}/api/face/toggles`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updates),
      });
    } catch {}
  }, [lk]);

  return (
    <>
    <ThreeColumn
      leftCollapsed={leftCollapsed}
      rightCollapsed={rightCollapsed}
      isMobile={isMobile}
      leftLabel="Profiles"
      rightLabel="Enhance"
      left={
        <LeftFacePanel
          profiles={profiles}
          uploading={uploading}
          uploadMsg={uploadMsg}
          onUpload={handleUpload}
          onActivate={handleActivateProfile}
        />
      }
      center={
        <CenterPreview
          videoRef={videoRef}
          outputRef={outputRef}
          processedVideoRef={lk.processedVideoRef}
          hasStream={hasStream}
          swapping={effectiveSwapping}
          fps={effectiveFps}
          frames={effectiveFrames}
          canSwap={canSwap}
          activeProfile={activeProfile}
          swapMode={swapMode}
          lkStatus={lk.status}
          lkError={lk.error}
          devices={devices}
          selectedDeviceId={selectedDeviceId}
          onSelectCamera={handleSelectCamera}
          vcamActive={isLiteEdition() ? isProjectorOpen : vcamActive}
          onToggleVCam={handleToggleVCam}
          onTogglePiP={handleTogglePiP}
          isPiPActive={isPiPActive}
          vmicActive={vmicActive}
          onToggleVMic={handleToggleVMic}
          onSwapModeChange={setSwapMode}
          onStartWebcam={() => startWebcam()}
          onStopWebcam={stopWebcam}
          onStartSwap={handleStartSwap}
          onStopSwap={handleStopSwap}
        />
      }
      right={
        <RightFacePanel
          enhanceFace={enhanceFace}
          gfpganQuality={gfpganQuality}
          enhancerType={enhancerType}
          codeformerFidelity={codeformerFidelity}
          autoEnhancer={autoEnhancer}
          eyedropperActive={eyedropperActive}
          activeProfile={activeProfile}
          hardware={hardware}
          onEnhanceFaceToggle={() => {
            const next = !enhanceFace;
            setEnhanceFace(next);
            updateToggle({ enhance_face: next });
          }}
          onGfpganQualityChange={(q: "fast" | "high") => {
            setGfpganQuality(q);
            updateToggle({ gfpgan_quality: q });
          }}
          onEnhancerChange={(e: EnhancerType) => {
            setEnhancerType(e);
            updateToggle({ enhancer: e });
          }}
          onFidelityChange={(v: number) => {
            setCfFidelity(v);
            updateToggle({ codeformer_fidelity: v });
          }}
          onEyedropperToggle={() => setEyedropperActive(v => !v)}
          onSkinTonePick={async (x: number, y: number) => {
            if (!activeProfile) return;
            setEyedropperActive(false);
            // 1. Send via LiveKit DataChannel (Zero-Inbound)
            await lk.sendControlMessage("set_skin_tone", {
              profile_id: activeProfile.profile_id,
              x,
              y,
            });
            // 2. Also send via REST (Local Studio mode)
            try {
              await fetch(`${API_BASE()}/api/face/profiles/${activeProfile.profile_id}/skin-tone`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ x, y }),
              });
            } catch {}
          }}

        />
      }
    />
    <StudioFeatureModal
      isOpen={showStudioModal}
      onClose={() => setShowStudioModal(false)}
      featureTitle="Virtual Audio Routing & Voice Output"
    />
    <CameraPermissionGuard
      isOpen={showCameraGuard}
      onClose={() => setShowCameraGuard(false)}
      onRetry={() => {
        setShowCameraGuard(false);
        startWebcam();
      }}
      errorType={cameraErrorType}
    />
    </>
  );
}
